import {
  LineItemTypes,
  OrderStatus,
  ReorderSuggestionStatus,
} from '@parallel-fluidics/shared/constants';
import { reorderSuggestionFromFirestore } from '@parallel-fluidics/shared/models/inventory';
import { pPartFromFirestore } from '@parallel-fluidics/shared/models/projects/parts';
import { formatDate, getShipDateByDays } from '@parallel-fluidics/shared/shipping';
import { buildLineItemSteps } from 'components/Orders/manageOrder/lineItemSteps';
import fetchProduct from 'features/products/apis/fetchProduct';
import fetchProductVersion from 'features/products/apis/fetchProductVersion';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
} from 'firebase/firestore';

export default async function fetchReorderOrderInitData(reorderSuggestionId, requestedByUserId) {
  const db = getFirestore();
  const suggestionSnap = await getDoc(doc(db, 'reorderSuggestions', reorderSuggestionId));
  if (!suggestionSnap.exists()) {
    throw new Error('Reorder suggestion not found.');
  }

  const suggestion = reorderSuggestionFromFirestore(suggestionSnap);
  if (suggestion.status !== ReorderSuggestionStatus.OPEN) {
    throw new Error('Reorder suggestion is no longer open.');
  }

  const [product, version] = await Promise.all([
    fetchProduct(suggestion.productDocId),
    fetchProductVersion(suggestion.productDocId, suggestion.productVersionId),
  ]);

  if (product.validation === 'failed' || version.validation === 'failed') {
    throw new Error('Product or version data is invalid.');
  }
  if (!version.projectDocId || !version.projectId || !version.pPartId) {
    throw new Error('Product version is missing internal project or part mapping.');
  }

  const [partsSnap, edgesSnap] = await Promise.all([
    getDocs(query(
      collection(db, 'projects', version.projectDocId, 'parts'),
      orderBy('partId', 'desc'),
    )),
    getDocs(collection(db, 'projects', version.projectDocId, 'edges')),
  ]);

  const parts = partsSnap.docs.map((partDoc) => pPartFromFirestore(partDoc));
  const edges = edgesSnap.docs.map((edgeDoc) => ({ id: edgeDoc.id, ...edgeDoc.data() }));
  const steps = buildLineItemSteps({
    parts,
    edges,
    pPartId: version.pPartId,
    quantity: suggestion.suggestedQuantity,
  });

  if (!steps.length) {
    throw new Error('Could not populate steps for this product version part.');
  }

  const targetShipDate = formatDate(getShipDateByDays(version.leadtimeDays ?? 0));

  return {
    isInternal: true,
    uid: requestedByUserId,
    orderDate: formatDate(new Date()),
    status: OrderStatus.NOT_STARTED,
    purchaseOrder: [],
    name: `Reorder ${product.sku} ${version.id}`,
    shipments: [{
      name: '',
      targetShipDate,
      shippingMethod: '',
      isShipped: false,
      lineItems: [{
        type: LineItemTypes.ON_DEMAND,
        projectId: version.projectId,
        projectDocId: version.projectDocId,
        pPartId: version.pPartId,
        pPartDocId: version.pPartDocId || steps[steps.length - 1]?.pPartDocId || null,
        quantity: suggestion.suggestedQuantity,
        bonding: '',
        notes: `Created from reorder suggestion ${suggestion.id}. SKU: ${product.sku}, version: ${version.id}.`,
        name: `${product.sku} ${version.id}`,
        desc: '',
        kickoffDoc: '',
        reorderSuggestionId: suggestion.id,
        steps,
      }],
      amount: '',
    }],
  };
}
